import { EnvironmentInjector, Injectable, runInInjectionContext } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { EMPTY, Observable, Subject, catchError, concatMap, firstValueFrom, from, of, switchMap, take, throwError } from "rxjs";
import { environment, UserService } from "@mypxplat/xplat/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { increment, arrayUnion, arrayRemove, serverTimestamp, deleteField } from "@angular/fire/firestore";

@Injectable()
export class FirebaseService {
  public fbUser: any;
  private _logginFBUserIn: boolean = false;
  public firbeaseUserLoggedIn$: Subject<boolean> = new Subject();

  constructor(
    public auth: AngularFireAuth,
    public userService: UserService,
    public db: AngularFirestore,
    private injector: EnvironmentInjector
  ) {
    this.auth.onAuthStateChanged((credential) => {
      this.fbUser = credential;
    });
  }

  // Wrapper for db.collection() that runs in injection context (needed for Angular 21 + @angular/fire compat)
  collection<T = any>(path: string, queryFn?: (ref: any) => any) {
    return runInInjectionContext(this.injector, () => this.db.collection<T>(path, queryFn));
  }

  // Wrapper for db.doc() that runs in injection context (needed for Angular 21 + @angular/fire compat)
  doc(path: string) {
    if (!path || path.includes("undefined") || path.includes("null")) {
      throw new Error(`FirebaseService.doc() called with invalid path: "${path}"`);
    }
    return runInInjectionContext(this.injector, () => this.db.doc(path));
  }

  // FieldValue helpers using @angular/fire's exports (should be compatible with compat layer)
  increment(n: number) {
    return increment(n);
  }

  arrayUnion(...elements: any[]) {
    return arrayUnion(...elements);
  }

  arrayRemove(...elements: any[]) {
    return arrayRemove(...elements);
  }

  serverTimestamp() {
    return serverTimestamp();
  }

  deleteField() {
    return deleteField();
  }

  // Update document using the native Firestore reference from AngularFirestore
  updateDocument(path: string, data: any): Promise<void> {
    return runInInjectionContext(this.injector, () => {
      // Access the native Firestore from AngularFirestore and use its update method
      return this.db.doc(path).ref.update(data);
    });
  }

  async handleFirestorePromise<T>(request: () => Promise<T>): Promise<T> {
    // Wrap request() in runInInjectionContext to ensure AngularFire's inject() calls work
    const wrappedRequest = () => runInInjectionContext(this.injector, request);
    try {
      return await wrappedRequest();
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error)); // Ensure error is an instance of Error
      if (normalizedError["code"] === "permission-denied") {
        // If the user is actively logging out OR has already logged out, don't try to re-login
        // This prevents "invalid-custom-token" errors when Firebase login is attempted with stale tokens
        if (this.userService.loggingOut || !this.userService.user) {
          throw normalizedError;
        }
        const user = await firstValueFrom(this.auth.user.pipe(take(1))); // Replace toPromise with firstValueFrom
        if (!user) {
          // Double-check user hasn't logged out during the async check (race condition)
          if (!this.userService.user) {
            throw normalizedError;
          }
          await this.loginFirebaseUser();
          return await wrappedRequest(); // Retry the request after login
        }
      }
      throw normalizedError; // Rethrow the normalized error
    }
  }

  public checkForSplash(productStringId?: string, splashId?: string) {
    return new Promise((resolve, reject) => {
      if (splashId) {
        this.handleFirestorePromise(() => this.db.collection("product_splashes").doc(splashId).ref.get())
          .then((results) => {
            if (results.exists) {
              let data: any = results.data();
              data.string_id = results.id;
              data.id = results.id;
              resolve(data);
            } else {
              resolve(false);
            }
          })
          .catch((error) => {
            reject(error instanceof Error ? error : new Error(String(error)));
          });
      } else if (productStringId) {
        this.handleFirestorePromise(() => this.db.collection("product_splashes").ref.where("string_ids", "array-contains", productStringId).get())
          .then((results) => {
            let allResults = results.docs.map((result) => {
              let data: any = result.data() || {};
              return { ...data, id: result.id, string_id: result.id };
            });
            let subOnlyResults = allResults.filter((result) => result.id.indexOf("_sphere") > -1);
            let devResults = allResults.filter((result) => result.id.indexOf("_dev") > -1);

            if (devResults.length && !environment.production) {
              resolve(devResults[0]);
            } else if (this.userService.user.active_subscription && subOnlyResults.length > 0) {
              resolve(subOnlyResults[0]);
            } else if (allResults.length > 0) {
              resolve(allResults[0]);
            } else {
              resolve(false);
            }
          })
          .catch((error) => {
            reject(error instanceof Error ? error : new Error(String(error)));
          });
      } else {
        resolve(false);
      }
    });
  }

  handleFirestoreObservable<T>(request: () => Observable<T>): Observable<T> {
    // Wrap request() in runInInjectionContext to ensure AngularFire's inject() calls work
    const wrappedRequest = () => runInInjectionContext(this.injector, request);
    return wrappedRequest().pipe(
      catchError((error: any) => {
        if (error.code === "permission-denied") {
          // If the user is actively logging out OR has already logged out, silently complete
          // This prevents Bugsnag errors when Firebase subscriptions fail during/after logout
          // The loggingOut flag is true during logout, but gets reset to false after completion
          // while subscriptions may still be cleaning up - so we also check !user
          if (this.userService.loggingOut || !this.userService.user) {
            return EMPTY;
          }
          return from(firstValueFrom(this.auth.user.pipe(take(1)))).pipe(
            switchMap(async (user) => {
              if (!user) {
                // Check if the application user is logged out
                // This can happen due to a race condition where logout completes
                // after the initial check at line 134 but before this async check runs
                if (!this.userService.user) {
                  // User logged out during async check - silently complete (same as line 134-135)
                  return EMPTY;
                }
                try {
                  await this.loginFirebaseUser();
                } catch {
                  // Re-login failed: the Firebase custom-token session could not be restored
                  // (e.g. the auth session expired mid-use — signInWithCustomToken returns 400
                  // "auth/invalid-custom-token" and paeapi /user returns 401). We can't retry the
                  // read, and this is an expected auth-lapse rather than a code defect, so complete
                  // silently instead of re-throwing an unhandled "Permission denied" to Bugsnag.
                  // Bugsnag: 6aa424bace44e305ca79311e
                  return EMPTY;
                }
                // The retried listener is a brand-new observable with no error handling of its own,
                // and callers routinely subscribe to live Firestore watchers with no error callback
                // (e.g. the community feed watchers end in a bare `.subscribe()`). If this retry is
                // ALSO denied — the restored custom-token session still can't read, or the user logs
                // out during recovery — the raw permission-denied would escape as an unhandled
                // "Missing or insufficient permissions." FirebaseError to Bugsnag (rethrown from the
                // Firestore listen callback). Guard the retry the same way the initial read is guarded:
                // degrade silently rather than crash a background listener. Bugsnag: 6ac0db9e396a2e952002f34f
                return wrappedRequest().pipe(
                  catchError((retryError: any) => {
                    if (retryError?.code === "permission-denied" || !this.userService.user) {
                      return EMPTY;
                    }
                    return throwError(() => new Error("Permission denied"));
                  })
                );
              }
              // User is logged in but still got permission denied - can't recover
              throw new Error("Permission denied");
            }),
            catchError(() => {
              // If logout happened during the async operation, silently complete
              if (!this.userService.user) {
                return EMPTY;
              }
              return throwError(() => new Error("Permission denied"));
            })
          );
        }
        return throwError(() => new Error(error || "Unknown error")); // Wrap all errors properly
      }),
      switchMap((result) => (result instanceof Observable ? result : of(result)))
    );
  }

  private _currentPromise: Promise<any> | null = null;

  /**
   * True when a Firebase custom-token sign-in should be skipped because the app user is
   * logging out or already logged out. Mirrors the guards in handleFirestorePromise /
   * handleFirestoreObservable so we never call signInWithCustomToken with a stale/cleared
   * token during logout — which surfaces as "auth/invalid-custom-token" (the custom token
   * format is incorrect) and is reported to Bugsnag as an unhandled rejection on /logout.
   * Bugsnag: 6a9b2a945ab98605a93318bf
   */
  private _shouldSkipFirebaseLogin(): boolean {
    return this.userService.loggingOut || !this.userService.user;
  }

  public loginFirebaseUser() {
    if (this._currentPromise) {
      return this._currentPromise;
    }
    this._currentPromise = new Promise((resolve, reject) => {
      // If the user is logging out (or already logged out), don't attempt a Firebase
      // re-login. Resolve gracefully instead of rejecting because several callers invoke
      // this fire-and-forget (e.g. sphere-onboarding-stripe) and a rejection would surface
      // as a brand-new unhandled promise rejection. Bugsnag: 6a9b2a945ab98605a93318bf
      if (this._shouldSkipFirebaseLogin()) {
        resolve(false);
        return;
      }
      if (!this._logginFBUserIn) {
        this._logginFBUserIn = true;
        if (this.fbUser && this.fbUser.uid == this.userService.user.id) {
          this._logginFBUserIn = false;
          this.firbeaseUserLoggedIn$.next(true);
          resolve(true);
        } else {
          this.auth.user.pipe(take(1)).subscribe({
            next: (credential) => {
              this.fbUser = credential;
              if (!this.fbUser || this.fbUser.uid != this.userService.user.id) {
                if (!this.userService.user || !this.userService.user.firebase_token) {
                  this.userService.getUserDetails(false).subscribe((userDetails) => {
                    // Re-check after the async fetch: logout may have started in the meantime,
                    // or the backend may have returned an empty token during session teardown.
                    // Signing in with a missing/stale token throws auth/invalid-custom-token.
                    // Bugsnag: 6a9b2a945ab98605a93318bf
                    if (this._shouldSkipFirebaseLogin() || !userDetails?.firebase_token) {
                      this._logginFBUserIn = false;
                      resolve(false);
                      return;
                    }
                    this.auth
                      .signInWithCustomToken(userDetails.firebase_token)
                      .then((result) => {
                        this.firbeaseUserLoggedIn$.next(true);
                        this._logginFBUserIn = false;
                        resolve(true);
                      })
                      .catch((error) => {
                        // Bugsnag Error: 6a56a870fdca5cb0d64760fe
                        // If the freshly-fetched token is also invalid, retry once more with a new token
                        if (error?.code === "auth/invalid-custom-token") {
                          this.userService.getUserDetails(false).subscribe({
                            next: (retryUserDetails) => {
                              // Don't retry the sign-in if logout began during the refetch or the
                              // refreshed token is empty. Bugsnag: 6a9b2a945ab98605a93318bf
                              if (this._shouldSkipFirebaseLogin() || !retryUserDetails?.firebase_token) {
                                this._logginFBUserIn = false;
                                resolve(false);
                                return;
                              }
                              this.auth
                                .signInWithCustomToken(retryUserDetails.firebase_token)
                                .then((retryResult) => {
                                  this.firbeaseUserLoggedIn$.next(true);
                                  this._logginFBUserIn = false;
                                  resolve(true);
                                })
                                .catch((retryError) => {
                                  this._logginFBUserIn = false;
                                  this._currentPromise = null;
                                  reject(new Error(retryError?.message || "An error occurred while logging in."));
                                });
                            },
                            error: (fetchError) => {
                              this._logginFBUserIn = false;
                              this._currentPromise = null;
                              reject(new Error(fetchError?.message || "Failed to refresh Firebase token."));
                            },
                          });
                        } else {
                          this._logginFBUserIn = false;
                          this._currentPromise = null;
                          reject(new Error(error?.message || "An error occurred while logging in."));
                        }
                      });
                  });
                } else {
                  // The auth.user emission is async, so logout may have started since the top-level
                  // guard. Re-check before signing in with the cached token to avoid the
                  // auth/invalid-custom-token error on /logout. Bugsnag: 6a9b2a945ab98605a93318bf
                  if (this._shouldSkipFirebaseLogin()) {
                    this._logginFBUserIn = false;
                    resolve(false);
                    return;
                  }
                  this.auth
                    .signInWithCustomToken(this.userService.user.firebase_token)
                    .then((result) => {
                      this.firbeaseUserLoggedIn$.next(true);
                      this._logginFBUserIn = false;
                      resolve(true);
                    })
                    .catch((error) => {
                      // If the cached token is invalid/expired, fetch a fresh token and retry
                      // Bugsnag Error: 69d97139e1c56b08d1c00a34
                      if (error?.code === "auth/invalid-custom-token") {
                        this.userService.getUserDetails(false).subscribe({
                          next: (userDetails) => {
                            // Don't retry the sign-in if logout began during the refetch or the
                            // refreshed token is empty. Bugsnag: 6a9b2a945ab98605a93318bf
                            if (this._shouldSkipFirebaseLogin() || !userDetails?.firebase_token) {
                              this._logginFBUserIn = false;
                              resolve(false);
                              return;
                            }
                            this.auth
                              .signInWithCustomToken(userDetails.firebase_token)
                              .then((retryResult) => {
                                this.firbeaseUserLoggedIn$.next(true);
                                this._logginFBUserIn = false;
                                resolve(true);
                              })
                              .catch((retryError) => {
                                this._logginFBUserIn = false;
                                this._currentPromise = null;
                                reject(new Error(retryError?.message || "An error occurred while logging in."));
                              });
                          },
                          error: (fetchError) => {
                            this._logginFBUserIn = false;
                            this._currentPromise = null;
                            reject(new Error(fetchError?.message || "Failed to refresh Firebase token."));
                          },
                        });
                      } else {
                        this._logginFBUserIn = false;
                        reject(new Error(error?.message || "An error occurred while logging in."));
                      }
                    });
                }
              } else {
                this.firbeaseUserLoggedIn$.next(true);
                this._logginFBUserIn = false;
                resolve(true);
              }
            },
            error: (err) => {
              reject(new Error(err?.message || "An error occurred while logging in."));
            },
          });
        }
      } else {
        reject(new Error("User is already logging in."));
      }
    });

    this._currentPromise.then(
      () => {
        this._currentPromise = null;
      },
      () => {
        this._currentPromise = null;
      }
    );

    return this._currentPromise;
  }
}
